import { Plate } from '../components/Card';
import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import api from '../api';

const fmt = n => (n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

// БУХГАЛТЕРИЯ цвета
const BUH_PURPLE = '#00a7dc';
const BUH_BLUE   = '#00a7dc';
const BUH_TEAL   = '#0e9f6e';

const BCard = ({ children, style }) => (
  <div style={{
    background: 'var(--bg2)',
    borderRadius: 12,
    border: '1px solid var(--border)',
    padding: '20px 24px',
    ...style,
  }}>
    {children}
  </div>
);

const SectionTitle = ({ icon, label, badge, color = BUH_PURPLE }) => (
  <div style={{ fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    <span style={{
      background: color, borderRadius: 8, padding: '4px 10px',
      fontSize: 12, color: '#fff', fontWeight: 900,
    }}>{icon}</span>
    <span style={{ color: 'var(--text1)' }}>{label}</span>
    {badge != null && (
      <span style={{ marginLeft: 4, fontSize: 12, color: color, fontWeight: 600 }}>{badge}</span>
    )}
  </div>
);

const KPIBox = ({ icon, label, value, sub, color }) => (
  <div style={{
    flex: '1 1 150px',
    background: 'var(--bg3)',
    borderRadius: 14, padding: '16px 18px 18px',
  }}>
    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{sub}</div>}
  </div>
);

const PIE_COLORS = ['#00a7dc', '#00a7dc', '#0e9f6e', '#e08a00', '#e04848', '#0e9f6e'];

export default function Accounting() {
  const [status, setStatus]       = useState(null);
  const [cp, setCp]               = useState(null);
  const [acts, setActs]           = useState(null);
  const [taxes, setTaxes]         = useState(null);
  const [income, setIncome]       = useState(null);
  const [activeTab, setActiveTab] = useState('counterparties');
  const [cpFilter, setCpFilter]   = useState('all');
  const [kindFilter, setKindFilter] = useState('ФЛ');
  const [search, setSearch]       = useState('');
  const [sortBy, setSortBy]       = useState('balance');

  useEffect(() => {
    api.get('/buh/status').then(r => setStatus(r.data)).catch(() => setStatus({ ok: false, error: 'Нет ответа' }));
    api.get('/buh/counterparties').then(r => setCp(r.data)).catch(() => setCp({ items: [], total_debt: 0, total_credit: 0 }));
    api.get('/buh/acts').then(r => setActs(r.data)).catch(() => setActs({ items: [] }));
    api.get('/buh/taxes').then(r => setTaxes(r.data)).catch(() => setTaxes({ items: [], total: 0 }));
    api.get('/buh/income').then(r => setIncome(r.data)).catch(() => setIncome({ income: 0, expense: 0, profit: 0, items: [] }));
  }, []);

  const allItems = cp?.items || [];
  const cpItems = allItems
    .filter(x =>
      (cpFilter === 'all' || x.type === cpFilter) &&
      (kindFilter === 'all' || x.kind === kindFilter) &&
      (!search || x.name.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'balance') return Math.abs(b.balance) - Math.abs(a.balance);
      if (sortBy === 'name') return a.name.localeCompare(b.name, 'ru');
      if (sortBy === 'debt') return b.debt - a.debt;
      return 0;
    });

  const withActs = allItems.filter(x => x.has_acts);

  const tabs = [
    { id: 'counterparties', label: '🤝 Партнёры' },
    { id: 'acts',           label: '📄 Акты сверок' },
    { id: 'taxes',          label: '🏛 Налоги' },
    { id: 'income',         label: '📊 Доходы/Расходы' },
  ];

  return (
    <div>
      <div className="kp-hero">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <Plate>1С:Fresh · бухгалтерия</Plate>
            <h1 style={{ margin: 0 }}>Бухгалтерия</h1>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
              Взаиморасчёты с партнёрами · Акты сверок · Налоговая отчётность · Доходы и расходы
            </div>
          </div>
          <div>
            {status === null && (
              <span style={{ background: 'rgba(224,138,0,0.12)', color: 'var(--yellow)', padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: '1px solid rgba(224,138,0,0.3)' }}>
                ⏳ Подключаемся…
              </span>
            )}
            {status?.ok && (
              <span style={{ background: 'rgba(14,159,110,0.12)', color: 'var(--green)', padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: '1px solid rgba(14,159,110,0.3)' }}>
                ● Подключено к 1С:Fresh
              </span>
            )}
            {status && !status.ok && (
              <div>
                <span style={{ background: 'rgba(224,72,72,0.12)', color: 'var(--red)', padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: '1px solid rgba(224,72,72,0.3)' }}>
                  ● Нет подключения
                </span>
                {status.error && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6, maxWidth: 300 }}>{status.error}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Предупреждение при отсутствии подключения */}
      {status && !status.ok && (
        <BCard style={{ marginBottom: 20, borderLeft: '4px solid var(--red)', background: 'rgba(224,72,72,0.06)' }}>
          <div style={{ fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>⚠️ Нет подключения к 1С:Fresh</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8 }}>
            Система автоматически ищет инфобазу на <b style={{ color: 'var(--text1)' }}>1cfresh.com</b><br />
            Ошибка: <code style={{ background: 'var(--bg3)', padding: '2px 6px', borderRadius: 4 }}>{status.error}</code><br /><br />
            <b>Что сделать:</b><br />
            1. Войдите на <a href="https://1cfresh.com/my" target="_blank" rel="noreferrer" style={{ color: BUH_BLUE }}>1cfresh.com/my</a><br />
            2. Откройте вашу бухгалтерию → нажмите «...» → «О программе»<br />
            3. Скопируйте URL инфобазы и добавьте в <code>.env</code>:<br />
            <code style={{ background: 'var(--bg3)', padding: '4px 8px', borderRadius: 4, display: 'block', marginTop: 6 }}>
              ONEC_BUH_BASE_URL=https://1cfresh.com/a/acc60/ВАШ_GUID/odata/standard.odata
            </code>
          </div>
        </BCard>
      )}

      {/* ── KPI ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <KPIBox icon="👥" label="Всего партнёров" value={allItems.length || '—'} color={BUH_PURPLE}
          sub={`ФЛ: ${allItems.filter(x=>x.kind==='ФЛ').length}  ИП: ${allItems.filter(x=>x.kind==='ИП').length}  ЮЛ: ${allItems.filter(x=>x.kind==='ЮЛ').length}`} />
        <KPIBox icon="📈" label="Нам должны" value={fmt(cp?.total_debt)} color="var(--green)"
          sub={`${allItems.filter(x => x.type === 'debtor').length} контрагентов`} />
        <KPIBox icon="📉" label="Мы должны" value={fmt(cp?.total_credit)} color="var(--red)"
          sub={`${allItems.filter(x => x.type === 'creditor').length} контрагентов`} />
        <KPIBox icon="✅" label="Прибыль" value={fmt(income?.profit)} color={income?.profit >= 0 ? 'var(--green)' : 'var(--red)'}
          sub={income?.profit >= 0 ? 'Доходы > Расходов' : 'Расходы > Доходов'} />
        <KPIBox icon="📄" label="Актов сверок" value={(acts?.items || []).length}
          color={BUH_TEAL} sub={`${(acts?.items || []).filter(x => x.posted).length} проведено`} />
      </div>

      {/* ── Вкладки ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            background: activeTab === t.id ? BUH_PURPLE : 'var(--bg3)',
            color: activeTab === t.id ? '#fff' : 'var(--text2)',
            transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ════════════════ ПАРТНЁРЫ ════════════════ */}
      {activeTab === 'counterparties' && (
        <BCard>
          <SectionTitle icon="🤝" label="Все контрагенты" color={BUH_PURPLE}
            badge={cp ? `${cpItems.length} из ${allItems.length}` : ''} />

          {/* Фильтры */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase' }}>Долг:</span>
            {[['all', 'Все'], ['debtor', '↑ Нам должны'], ['creditor', '↓ Мы должны'], ['neutral', 'Нет долга']].map(([val, lbl]) => (
              <button key={val} onClick={() => setCpFilter(val)} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: cpFilter === val ? BUH_PURPLE : 'var(--bg3)',
                color: cpFilter === val ? '#fff' : 'var(--text2)',
              }}>{lbl}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase' }}>Тип:</span>
            {[['all', 'Все'], ['ФЛ', 'Физлица'], ['ИП', 'ИП'], ['ЮЛ', 'Юрлица'], ['ГО', 'Госорганы']].map(([val, lbl]) => (
              <button key={val} onClick={() => setKindFilter(val)} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: kindFilter === val ? BUH_BLUE : 'var(--bg3)',
                color: kindFilter === val ? '#fff' : 'var(--text2)',
              }}>{lbl}</button>
            ))}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '5px 10px', borderRadius: 6, fontSize: 12 }}>
                <option value="balance">↕ По балансу</option>
                <option value="name">А-Я По имени</option>
                <option value="debt">↕ По задолженности</option>
              </select>
              <input placeholder="🔍 Поиск…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '5px 12px', borderRadius: 6, fontSize: 13, width: 200 }} />
            </div>
          </div>

          {/* Топ с долгами — бар */}
          {withActs.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>Топ по балансу (из актов сверок)</div>
              <ResponsiveContainer width="100%" height={withActs.length * 28 + 20} minHeight={100}>
                <BarChart data={withActs.filter(x => x.balance !== 0).slice(0, 10)} layout="vertical">
                  <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" width={180} tick={{ fill: 'var(--text2)', fontSize: 10 }} tickFormatter={v => v.length > 24 ? v.slice(0, 24) + '…' : v} />
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="balance" name="Баланс" radius={[0, 4, 4, 0]}>
                    {withActs.filter(x => x.balance !== 0).slice(0, 10).map((x, i) => (
                      <Cell key={i} fill={x.balance > 0 ? 'var(--green)' : 'var(--red)'} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Таблица всех контрагентов */}
          <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '7px 10px', textAlign: 'left',  color: 'var(--text2)', fontSize: 11, fontWeight: 700, width: 30 }}>#</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left',  color: 'var(--text2)', fontSize: 11, fontWeight: 700 }}>Контрагент</th>
                  <th style={{ padding: '7px 10px', textAlign: 'center',color: 'var(--text2)', fontSize: 11, fontWeight: 700, width: 50 }}>ТИП</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)', fontSize: 11, fontWeight: 700 }}>Задолж.</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)', fontSize: 11, fontWeight: 700 }}>Аванс</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)', fontSize: 11, fontWeight: 700 }}>Баланс</th>
                  <th style={{ padding: '7px 10px', textAlign: 'center',color: 'var(--text2)', fontSize: 11, fontWeight: 700 }}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {cpItems.map((x, i) => {
                  const kindColor = { ФЛ: '#00a7dc', ИП: '#00a7dc', ЮЛ: '#0e9f6e', ГО: '#e08a00' }[x.kind] || '#888';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '7px 10px', color: 'var(--text2)', fontSize: 11 }}>{i + 1}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 500, maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {x.name}
                        {x.inn && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text2)' }}>ИНН {x.inn}</span>}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                        <span style={{ background: kindColor + '22', color: kindColor, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{x.kind}</span>
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: x.debt > 0 ? 'var(--green)' : 'var(--text2)' }}>{x.debt > 0 ? fmt(x.debt) : '—'}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)' }}>{x.advance > 0 ? fmt(x.advance) : '—'}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700,
                        color: x.balance > 0 ? 'var(--green)' : x.balance < 0 ? 'var(--red)' : 'var(--text2)' }}>
                        {x.balance !== 0 ? fmt(x.balance) : '—'}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                        {x.type === 'debtor' && (
                          <span style={{ background: 'rgba(14,159,110,0.12)', color: 'var(--green)', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>↑ нам должны</span>
                        )}
                        {x.type === 'creditor' && (
                          <span style={{ background: 'rgba(224,72,72,0.12)', color: 'var(--red)', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>↓ мы должны</span>
                        )}
                        {x.type === 'neutral' && (
                          <span style={{ color: 'var(--text2)', fontSize: 10 }}>нет долга</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!cpItems.length && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                {cp ? 'Нет контрагентов по фильтру' : 'Загрузка…'}
              </div>
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text2)' }}>
            Показано: <b style={{ color: 'var(--text1)' }}>{cpItems.length}</b> из {allItems.length} · Балансы рассчитаны по актам сверок
          </div>
        </BCard>
      )}

      {/* ════════════════ АКТЫ СВЕРОК ════════════════ */}
      {activeTab === 'acts' && (
        <BCard>
          <SectionTitle icon="📄" label="Акты сверки взаиморасчётов" color={BUH_TEAL}
            badge={acts ? `${acts.items.length} документов` : ''} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Дата', 'Номер', 'Контрагент', 'Наш долг', 'Их долг', 'Статус'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(acts?.items || []).map((a, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{a.date}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>#{a.number}</td>
                    <td style={{ padding: '8px 12px', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.counterparty}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--red)' }}>{fmt(a.our_debt)}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--green)' }}>{fmt(a.their_debt)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        background: a.posted ? 'rgba(14,159,110,0.12)' : 'rgba(224,138,0,0.12)',
                        color: a.posted ? 'var(--green)' : 'var(--yellow)',
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      }}>
                        {a.posted ? '✓ Проведён' : '⏳ Черновик'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!acts?.items?.length && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                {status?.ok ? 'Нет актов сверок' : 'Ожидаем подключения к 1С:Fresh'}
              </div>
            )}
          </div>
        </BCard>
      )}

      {/* ════════════════ НАЛОГИ ════════════════ */}
      {activeTab === 'taxes' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <BCard>
            <SectionTitle icon="🏛" label="Расчёты с бюджетом" color={BUH_PURPLE}
              badge={taxes ? `Итого: ${fmt(taxes.total)}` : ''} />
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Налог / КБК', 'Сумма'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Сумма' ? 'right' : 'left', color: 'var(--text2)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(taxes?.items || []).map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '8px 12px' }}>{t.tax}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: t.amount > 0 ? 'var(--red)' : 'var(--green)' }}>
                        {fmt(Math.abs(t.amount))}
                        {t.amount > 0 && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--text2)' }}>к уплате</span>}
                        {t.amount < 0 && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--green)' }}>переплата</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!taxes?.items?.length && (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  {status?.ok ? 'Нет данных по налогам' : 'Ожидаем подключения к 1С:Fresh'}
                </div>
              )}
            </div>
          </BCard>

          {/* Диаграмма налогов */}
          {taxes?.items?.length > 0 && (
            <BCard>
              <SectionTitle icon="📊" label="Распределение налогов" color={BUH_PURPLE} />
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={taxes.items.map((t, i) => ({ name: t.tax, value: Math.abs(t.amount) }))}
                    cx="50%" cy="50%" outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name.slice(0, 12)}… ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {taxes.items.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </BCard>
          )}
        </div>
      )}

      {/* ════════════════ ДОХОДЫ / РАСХОДЫ ════════════════ */}
      {activeTab === 'income' && (
        <div>
          {/* KPI */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <KPIBox icon="💰" label="Доходы" value={fmt(income?.income)} color={BUH_TEAL} />
            <KPIBox icon="📦" label="Расходы" value={fmt(income?.expense)} color="var(--red)" />
            <KPIBox icon="✅" label="Прибыль" value={fmt(income?.profit)}
              color={income?.profit >= 0 ? 'var(--green)' : 'var(--red)'}
              sub={income?.income > 0 ? `Рентаб. ${((income.profit / income.income) * 100).toFixed(1)}%` : ''} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <BCard>
              <SectionTitle icon="📊" label="По статьям доходов/расходов" color={BUH_BLUE} />
              {income?.items?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={income.items.slice(0, 12)} layout="vertical">
                    <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 10 }} />
                    <YAxis type="category" dataKey="category" width={140} tick={{ fill: 'var(--text2)', fontSize: 10 }} tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v} />
                    <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="income" fill={BUH_TEAL} name="Доходы" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="expense" fill="var(--red)" name="Расходы" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                  {status?.ok ? 'Нет данных' : 'Ожидаем подключения к 1С:Fresh'}
                </div>
              )}
            </BCard>

            <BCard>
              <SectionTitle icon="📋" label="Детализация по статьям" color={BUH_BLUE} />
              <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Статья', 'Доходы', 'Расходы', 'Прибыль'].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: h === 'Статья' ? 'left' : 'right', color: 'var(--text2)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(income?.items || []).map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '7px 10px', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.category}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: BUH_TEAL }}>{fmt(it.income)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--red)' }}>{fmt(it.expense)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: it.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(it.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!income?.items?.length && (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text2)' }}>
                    {status?.ok ? 'Нет данных' : 'Ожидаем подключения к 1С:Fresh'}
                  </div>
                )}
              </div>
            </BCard>
          </div>
        </div>
      )}
    </div>
  );
}
